import { useSetToast } from "@app/components/Toast";
import { useUpdateObjective } from "@app/screens/Objectives/data";
import { EntityChip } from "@app/shared/components/Entity/EntityChip";
import EntitySelect from "@app/shared/components/Entity/EntitySelect";
import { SubthemeTag } from "@app/shared/components/Theme/SubthemeTag";
import { SubthemeTagSelect } from "@app/shared/components/Theme/SubthemeTagSelect";
import { ThemeTag } from "@app/shared/components/Theme/ThemeTag";
import { ThemeTagSelect } from "@app/shared/components/Theme/ThemeTagSelect";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import {
    ResponseStatus,
    type DeleteObjectiveTargetsMutation,
    type GetObjectiveQuery,
    type UpdateObjectiveMutation,
} from "@generated/client/graphql";
import { useMemo, useRef, useState, type FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDeleteObjectiveTargets } from "../../data";
import { isObjectiveEditable } from "../../services";
import { DefinitionGuidance } from "./DefinitionGuidance";
import { keys, RESOURCE_NAME } from "./i18n";
import { NameInput } from "./NameInput";
import { ObjectiveContent } from "./ObjectiveContent";

type ObjectiveHeaderProps = {
    objective: GetObjectiveQuery["objective"];
};

export const ObjectiveDefinition: FC<ObjectiveHeaderProps> = ({
    objective,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { setToastError } = useSetToast();

    const canEditObjective = useMemo(
        () => isObjectiveEditable(objective),
        [objective],
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const themeSubthemeTempRef = useRef<{
        themeId?: string;
        subthemeId?: string;
    }>({});

    const [themeId, setThemeId] = useOptimistic(objective.themeId);
    const [subthemeId, setSubthemeId] = useOptimistic(objective.subthemeId);
    const [entityId, setEntityId] = useOptimistic(objective.entityId);

    const updateObjectiveMutation = useUpdateObjective(
        objective?.id,
        objective?.entityId,
    );

    const updateEntityMutationOpts = {
        onSettled: (data: UpdateObjectiveMutation | undefined) => {
            if (data?.updateObjective?.status === ResponseStatus.Error) {
                setToastError(`${data.updateObjective.error?.message}`);
            }
        },
        onError: () => {
            setToastError(t(keys.error_update));
        },
    };

    const deleteObjectiveTargetsMutation = useDeleteObjectiveTargets(
        objective.id,
    );

    const updateTheme = (themeId: string) => {
        setThemeId(themeId);
        updateObjectiveMutation.mutate(
            { id: objective.id, themeId },
            updateEntityMutationOpts,
        );
    };

    const updateSubtheme = (subthemeId: string) => {
        setSubthemeId(subthemeId);
        updateObjectiveMutation.mutate(
            { id: objective.id, subthemeId },
            updateEntityMutationOpts,
        );
    };

    const handleThemeChange = (themeId: string) => {
        if (objective.themeId === themeId) return;

        /**
         * If the objective has an indicator, we need to open the modal
         * to confirm the change because it will reset the indicator selection
         * and the set targets
         */
        if (objective.indicatorId != null && objective.targets.length > 0) {
            setIsModalOpen(true);
            themeSubthemeTempRef.current = { themeId };
            return;
        }
        updateTheme(themeId);
    };

    const handleSubThemeChange = (subthemeId: string) => {
        if (objective.subthemeId === subthemeId) return;

        /**
         * If the objective has an indicator, we need to open the modal
         * to confirm the change because it will reset the indicator selection
         * and the set targets
         */
        if (objective.indicatorId != null && objective.targets.length > 0) {
            setIsModalOpen(true);
            themeSubthemeTempRef.current = { subthemeId };
            return;
        }
        updateSubtheme(subthemeId);
    };

    const handleEntityChange = (entityId: string) => {
        if (objective.entityId === entityId) return;
        setEntityId(entityId);
        updateObjectiveMutation.mutate(
            { id: objective.id, entityId },
            updateEntityMutationOpts,
        );
    };

    const handleModalConfirm = () => {
        if (themeSubthemeTempRef.current.themeId != null) {
            updateTheme(themeSubthemeTempRef.current.themeId);
        }
        if (themeSubthemeTempRef.current.subthemeId != null) {
            updateSubtheme(themeSubthemeTempRef.current.subthemeId);
        }
        deleteObjectiveTargetsMutation.mutate(
            objective.targets.map((target) => target.id),
            {
                onSettled: (
                    data: DeleteObjectiveTargetsMutation | undefined,
                    error,
                ) => {
                    if (
                        data?.deleteObjectiveTargets?.status ===
                        ResponseStatus.Error
                    ) {
                        setToastError(
                            `${data.deleteObjectiveTargets.error?.message}`,
                        );
                    }
                    if (error) {
                        setToastError(t(keys.error_delete));
                    }
                },
            },
        );
        updateObjectiveMutation.mutate(
            { id: objective.id, indicatorId: null },
            updateEntityMutationOpts,
        );
    };

    const handleModalCancel = () => {
        themeSubthemeTempRef.current = {};
        setIsModalOpen(false);
    };

    return (
        <FlexCol gap="6">
            <FlexCol gap="6" px="4" pt="6">
                <DefinitionGuidance />
                <NameInput
                    objective={objective}
                    editable={canEditObjective}
                    key={objective.id}
                />
                <FlexCol gap="2">
                    <Text color="secondary">{t(keys.theme_label)}</Text>
                    <FlexRow gap="4" alignItems="center">
                        <FlexRow gap="2">
                            {canEditObjective ? (
                                <EntitySelect
                                    entityId={entityId}
                                    setEntityId={handleEntityChange}
                                    trigger={
                                        <EntityChip
                                            entityId={entityId}
                                            size="md"
                                            isTrigger
                                        />
                                    }
                                />
                            ) : (
                                <EntityChip entityId={entityId} size="md" />
                            )}
                            {canEditObjective ? (
                                <ThemeTagSelect
                                    themeId={themeId}
                                    setThemeId={handleThemeChange}
                                    size="md"
                                />
                            ) : (
                                <ThemeTag themeId={themeId} size="md" />
                            )}
                            {canEditObjective ? (
                                <SubthemeTagSelect
                                    themeId={themeId}
                                    subthemeId={subthemeId || undefined}
                                    setSubthemeId={handleSubThemeChange}
                                    size="md"
                                />
                            ) : (
                                <SubthemeTag
                                    themeId={themeId}
                                    subthemeId={subthemeId ?? undefined}
                                    size="md"
                                />
                            )}
                        </FlexRow>
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Icon
                                    name="circleInfo"
                                    className="text-secondary"
                                    size="sm"
                                />
                            </Tooltip.Trigger>
                            <Tooltip.Content className="max-w-[400px]">
                                <Trans
                                    t={t}
                                    i18nKey={keys.hint_theme_subtheme}
                                    components={{
                                        br: (
                                            <br
                                                key="br-1"
                                                className="content-[''] block my-2"
                                            />
                                        ),
                                    }}
                                />
                            </Tooltip.Content>
                        </Tooltip>
                    </FlexRow>
                </FlexCol>
                <ObjectiveContent objective={objective} />
            </FlexCol>
            <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
                <Modal.Overlay>
                    <Modal.Content>
                        <Modal.Header>
                            <Modal.Title>
                                {t(keys.change_theme_subtheme_modal_title)}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {t(keys.change_theme_subtheme_modal_description)}
                        </Modal.Body>
                        <Modal.Footer>
                            <Modal.CancelCTA onClick={handleModalCancel}>
                                {t(keys.change_theme_subtheme_modal_cancel)}
                            </Modal.CancelCTA>
                            <Modal.ConfirmCTA
                                variant="flatDanger"
                                onClick={handleModalConfirm}
                            >
                                {t(keys.change_theme_subtheme_modal_cta)}
                            </Modal.ConfirmCTA>
                        </Modal.Footer>
                    </Modal.Content>
                </Modal.Overlay>
            </Modal>
        </FlexCol>
    );
};
